import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

/**
 * @typedef {object} QuickAddCacheEntry
 * @property {Promise<Element | null>} promise
 * @property {Element | null} grid
 * @property {boolean} settled
 */

/** @type {Map<string, QuickAddCacheEntry>} */
const quickAddGridCache = new Map();

let quickAddOpenRequest = 0;

/**
 * @param {string} productPageUrl
 * @returns {string}
 */
function toQuickAddViewUrl(productPageUrl) {
  if (!productPageUrl) return '';

  const url = new URL(productPageUrl, window.location.origin);
  url.searchParams.set('view', 'quick-add');
  return url.toString();
}

/**
 * @param {string} productPageUrl
 * @returns {Element | null}
 */
function getSettledQuickAddGrid(productPageUrl) {
  const requestUrl = toQuickAddViewUrl(productPageUrl);
  const entry = quickAddGridCache.get(requestUrl);
  if (!entry?.settled || !entry.grid) return null;
  return entry.grid;
}

/**
 * Fetches and caches the slim quick-add grid for a product URL.
 * In-flight requests for the same URL are shared and are not aborted.
 * @param {string} productPageUrl
 * @param {{ priority?: 'high' | 'low' | 'auto' }} [options]
 * @returns {Promise<Element | null>}
 */
function loadQuickAddGrid(productPageUrl, options = {}) {
  const requestUrl = toQuickAddViewUrl(productPageUrl);
  if (!requestUrl) return Promise.resolve(null);

  const existing = quickAddGridCache.get(requestUrl);
  if (existing) return existing.promise;

  /** @type {QuickAddCacheEntry} */
  const entry = {
    promise: Promise.resolve(null),
    grid: null,
    settled: false,
  };

  entry.promise = fetch(requestUrl, { priority: options.priority })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement) return null;

      const grid = /** @type {Element} */ (gridElement.cloneNode(true));
      entry.grid = grid;
      return grid;
    })
    .then((grid) => {
      if (!grid) quickAddGridCache.delete(requestUrl);
      return grid;
    })
    .catch((error) => {
      quickAddGridCache.delete(requestUrl);
      console.error(error);
      return null;
    })
    .finally(() => {
      entry.settled = true;
    });

  quickAddGridCache.set(requestUrl, entry);
  return entry.promise;
}

export class QuickAddComponent extends Component {

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click.
   * Opens the dialog immediately, then fills it from the cache or network.
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const requestId = ++quickAddOpenRequest;
    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    const cachedGrid = getSettledQuickAddGrid(currentUrl);

    if (cachedGrid) {
      await this.updateQuickAddModal(/** @type {Element} */ (cachedGrid.cloneNode(true)));
      if (requestId !== quickAddOpenRequest) return;
      this.#setQuickAddLoading(false);
      this.#openQuickAddModal();
      return;
    }

    this.#setQuickAddLoading(true);
    this.#openQuickAddModal();

    try {
      const productGrid = await this.fetchProductPage(currentUrl);
      if (requestId !== quickAddOpenRequest) return;

      if (!productGrid) {
        this.#setQuickAddLoading(false);
        this.#closeQuickAddModal();
        return;
      }

      await this.updateQuickAddModal(/** @type {Element} */ (productGrid.cloneNode(true)));
      if (requestId !== quickAddOpenRequest) return;
      this.#setQuickAddLoading(false);
    } catch (error) {
      if (requestId !== quickAddOpenRequest) return;
      console.error(error);
      this.#setQuickAddLoading(false);
      this.#closeQuickAddModal();
    }
  };

  /**
   * @param {boolean} isLoading
   */
  #setQuickAddLoading(isLoading) {
    const dialogComponent = document.getElementById('quick-add-dialog');
    const dialog = dialogComponent?.querySelector('dialog');
    if (!(dialog instanceof HTMLElement)) return;

    dialog.toggleAttribute('data-loading', isLoading);
    if (isLoading) {
      dialog.setAttribute('aria-busy', 'true');
    } else {
      dialog.removeAttribute('aria-busy');
    }
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the slim quick-add view for a product.
   * Repeated calls for the same URL share one request.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @param {{ priority?: 'high' | 'low' | 'auto' }} [options]
   * @returns {Promise<Element | null>}
   */
  fetchProductPage(productPageUrl, options = {}) {
    return loadQuickAddGrid(productPageUrl, options);
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Keep the dialog on regular product media. fibbl-video throws if it connects before its product is loaded.
    productGrid.querySelectorAll('fibbl-bar, fibbl-layer, [data-fibbl-video-id], fibbl-video').forEach((el) => el.remove());

    if (isMobileBreakpoint()) {
      this.#buildMobileQuickAddLayout(productGrid);
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Builds the mobile quick-add layout:
   * View full details → image → title/price + inventory → variants → buy (qty always 1)
   * @param {Element} productGrid - The product grid element
   */
  #buildMobileQuickAddLayout(productGrid) {
    const productMedia = productGrid.querySelector('.product-information__media');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const productInventory = productGrid.querySelector('product-inventory');
    const quantitySelector = productFormComponent?.querySelector('quantity-selector-component');

    const dialogComponent = document.getElementById('quick-add-dialog');
    const viewFullDetailsText =
      dialogComponent instanceof HTMLElement ? dialogComponent.dataset.viewFullDetailsText || '' : '';

    const viewFullDetails = document.createElement('a');
    viewFullDetails.href = this.productPageUrl;
    viewFullDetails.className = 'quick-add-modal__view-full-details';
    viewFullDetails.textContent = viewFullDetailsText;

    const productTitle = document.createElement('a');
    productTitle.textContent = this.dataset.productTitle || '';
    productTitle.href = this.productPageUrl;

    // Title + price on the left; stock status on the right
    const productHeaderInfo = document.createElement('div');
    productHeaderInfo.classList.add('product-header__info');
    productHeaderInfo.appendChild(productTitle);
    if (productPrice) {
      productHeaderInfo.appendChild(productPrice);
    }

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');
    productHeader.appendChild(productHeaderInfo);
    if (productInventory) {
      productHeader.appendChild(productInventory);
    }

    // Always add 1 — replace quantity UI with a hidden input
    if (quantitySelector) {
      const hiddenQuantity = document.createElement('input');
      hiddenQuantity.type = 'hidden';
      hiddenQuantity.name = 'quantity';
      hiddenQuantity.value = '1';
      quantitySelector.replaceWith(hiddenQuantity);
    }

    this.#prepareMobileMediaGallery(productMedia);

    /** @type {Element[]} */
    const mobileChildren = [viewFullDetails];
    if (productMedia) mobileChildren.push(productMedia);
    mobileChildren.push(productHeader);
    if (variantPicker) mobileChildren.push(variantPicker);
    if (productFormComponent) mobileChildren.push(productFormComponent);

    productGrid.replaceChildren(...mobileChildren);
  }

  /**
   * Prepares the media gallery for mobile quick buy:
   * single full-width image, chevron controls beside the image, no peeking slides.
   * @param {Element | null} productMedia - The product media container
   */
  #prepareMobileMediaGallery(productMedia) {
    if (!productMedia) return;

    productMedia.querySelectorAll('media-gallery').forEach((gallery) => {
      gallery.classList.remove('media-gallery--hint');
    });

    // Instant selection avoids the slideshow's animated DOM-reorder path, which
    // can leave the gallery permanently disabled after a few thumbnail jumps in
    // this height-constrained mobile layout.
    productMedia.querySelectorAll('slideshow-component').forEach((slideshow) => {
      slideshow.setAttribute('prefer-instant', '');
    });

    const chevronTemplate = document.getElementById('quick-add-chevron-icon');

    productMedia.querySelectorAll('slideshow-arrows').forEach((arrows) => {
      arrows.classList.remove('mobile:hidden');
      arrows.setAttribute('position', 'center');

      arrows.querySelectorAll('.slideshow-control').forEach((control) => {
        control.classList.remove('slideshow-control--style-none', 'slideshow-control--style-arrow');
        control.classList.add('slideshow-control--style-chevron');

        if (!(chevronTemplate instanceof HTMLTemplateElement)) return;

        const icon = chevronTemplate.content.firstElementChild?.cloneNode(true);
        const existingIcon = control.querySelector('.svg-wrapper');
        if (icon && existingIcon) {
          existingIcon.replaceWith(icon);
        }
      });
    });
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
    const viewFullDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.quick-add-modal__view-full-details')
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    if (viewFullDetailsLink) viewFullDetailsLink.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
